import classNames from 'classnames';
import React, { useEffect } from 'react';
import type { DeepRequired, FieldErrorsImpl, FieldValues } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';
import type { Days, ScheduleDayModel, ScheduleModel } from 'entities/campaign';
import { Checkbox } from 'shared/ui/Checkbox';
import { getInputError } from 'shared/lib/form';
import type { Option } from 'shared/ui/Select';
import { FormSelect } from 'shared/ui/Select';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import { getKeys } from 'shared/lib/object';
import { ContentCopyButton } from 'shared/ui/ContentCopyButton';

const SCHEDULE_PREFIX = 'schedule.';

interface SchedulingDayChangeableProps {
  index: number;
  name: Days;
  period: ScheduleDayModel;
  allowCopySchedule?: boolean;
  className?: string;
  timeOptions: Option[];
  schedule: ScheduleModel;
}

export function SchedulingDayChangeable({
  name,
  period,
  className,
  timeOptions,
  schedule,
  index,
  allowCopySchedule = true,
}: SchedulingDayChangeableProps) {
  const {
    formState: { errors },
    clearErrors,
    setValue,
  } = useFormContext();
  const timeFromName = `${name}.timeFrom`;
  const timeToName = `${name}.timeTo`;
  const error = getInputError(errors.schedule as FieldErrorsImpl<DeepRequired<FieldValues>>, name);
  const fromTimeValues = useWatch({ name: [timeFromName, timeToName] });
  const mondayTimeValue = useWatch({ name: ['Monday.timeFrom', 'Monday.timeTo'] });
  const mondayIsSelected = useWatch({ name: 'Monday.isSelected' });
  const days = getKeys(schedule).filter((day) => day !== 'Monday');

  const isSelected = useWatch({ name: `${name}.isSelected` });
  const showCopyBtn = name === 'Monday' && allowCopySchedule;

  const handleCopy = () => {
    days.forEach((day) => {
      setValue(`${day}.timeFrom`, mondayTimeValue[0]);
      setValue(`${day}.timeTo`, mondayTimeValue[1]);
      setValue(`${day}.isSelected`, mondayIsSelected);
    });
  };

  useEffect(() => {
    clearErrors(SCHEDULE_PREFIX + name);
  }, [fromTimeValues]);

  return (
    <div className={classNames('flex justify-start items-start', className)}>
      <Checkbox
        className="mt-2 w-[54px] min-w-[54px]"
        name={`${name}.isSelected`}
        {...getDataAutoTestAttributes([`checkbox-scheduling-day-${index + 1}`])}
      >
        <span className="text-sm">{name.slice(0, 3)}</span>
      </Checkbox>
      <div className={`ml-4 flex items-start justify-start ${!showCopyBtn && 'pr-[48px]'}`}>
        <div className="flex flex-col justify-start">
          <div className="flex justify-start">
            <FormSelect
              className="w-[107px]"
              isRequired
              title={`${name} from`}
              name={timeFromName}
              errorName={SCHEDULE_PREFIX + timeFromName}
              options={timeOptions}
              selectedOptionId={period.timeFrom}
              isDisabled={!isSelected}
              isErrorMessageHidden
              isSmall
              hideIcon
              {...getDataAutoTestAttributes([`select-scheduling-day-from-${index + 1}`])}
            />
            <div className="ml-2">
              <FormSelect
                className="w-[107px]"
                isRequired
                title={`${name} to`}
                name={timeToName}
                errorName={SCHEDULE_PREFIX + timeToName}
                options={timeOptions}
                selectedOptionId={period.timeTo}
                isDisabled={!isSelected}
                isErrorMessageHidden
                isSmall
                hideIcon
                {...getDataAutoTestAttributes([`select-scheduling-day-to-${index + 1}`])}
              />
            </div>
          </div>
          {error && <div className="ml-4 mt-1 text-xs font-medium text-red-500">{error}</div>}
        </div>
        {showCopyBtn && (
          <ContentCopyButton
            content="Copy to all"
            onClick={handleCopy}
            className="ml-4 mt-[2px]"
          />
        )}
      </div>
    </div>
  );
}
